/* eslint-disable @typescript-eslint/explicit-function-return-type */
import type { Component, JSX } from 'solid-js';
import { createEffect, createSignal } from 'solid-js';

export interface CustomInputProps {
  name: string;
  label: string;

  // Common input attributes
  id?: string;
  value?: string;
  placeholder?: string;
  type?: string;
  disabled?: boolean;
  readonly?: boolean;
  required?: boolean;
  class?: string; // Support for custom CSS classes
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'data-cy'?: string; // support direct attribute passthrough
  dataCy?: string; // alternative prop name that maps to data-cy
  autocomplete?: boolean;

  // Validation-like props (replacing Formik meta)
  error?: string;
  touched?: boolean;

  // Callback to expose input ref for imperative access
  onInputRef?(ref: HTMLInputElement | undefined): void;

  // Event callbacks (optional)
  onInput?(
    event: InputEvent & { currentTarget: HTMLInputElement; target: Element }
  ): void;
  onChange?(
    event: Event & { currentTarget: HTMLInputElement; target: Element }
  ): void;
  onBlur?(
    event: FocusEvent & { currentTarget: HTMLInputElement; target: Element }
  ): void;
  onFocus?(
    event: FocusEvent & { currentTarget: HTMLInputElement; target: Element }
  ): void;
  onKeyDown?(
    event: KeyboardEvent & { currentTarget: HTMLInputElement; target: Element }
  ): void;
}

const CustomInput: Component<CustomInputProps> = (props) => {
  let inputRef: HTMLInputElement | undefined;
  const [innerValue, setInnerValue] = createSignal<string>('');

  // Expose input ref for imperative access
  createEffect(() => {
    props.onInputRef?.(inputRef);
  });

  const getId = () => props.id ?? props.name;
  const getDataCy = () => props.dataCy ?? props['data-cy'] ?? props.name;
  const getType = () => props.type ?? 'text';
  const getValue = () =>
    props.value !== undefined ? props.value : innerValue();
  const hasError = () => Boolean(props.touched && props.error);
  const inputClass = () => (hasError() ? 'error' : undefined);

  const emit = (
    eventName: string,
    event: Event,
    detail?: Record<string, unknown>
  ): void => {
    const customEvent = new CustomEvent(eventName, {
      detail: { ...(detail ?? {}), originalEvent: event },
      bubbles: true,
    });
    (event.target as HTMLElement | null)?.dispatchEvent(customEvent);
  };

  const handleInput: JSX.EventHandlerUnion<HTMLInputElement, InputEvent> = (
    e
  ) => {
    const target = e.currentTarget as HTMLInputElement;
    setInnerValue(target.value);
    emit('tc-custominput-input', e as unknown as Event, {
      value: target.value,
    });
    props.onInput?.(
      e as unknown as InputEvent & {
        currentTarget: HTMLInputElement;
        target: Element;
      }
    );
  };

  const handleChange: JSX.EventHandlerUnion<HTMLInputElement, Event> = (e) => {
    const target = e.currentTarget as HTMLInputElement;
    emit('tc-custominput-change', e as Event, { value: target.value });
    props.onChange?.(
      e as Event & { currentTarget: HTMLInputElement; target: Element }
    );
  };

  const handleBlur: JSX.EventHandlerUnion<HTMLInputElement, FocusEvent> = (
    e
  ) => {
    emit('tc-custominput-blur', e as FocusEvent);
    props.onBlur?.(
      e as FocusEvent & { currentTarget: HTMLInputElement; target: Element }
    );
  };

  const handleFocus: JSX.EventHandlerUnion<HTMLInputElement, FocusEvent> = (
    e
  ) => {
    emit('tc-custominput-focus', e as FocusEvent);
    props.onFocus?.(
      e as FocusEvent & { currentTarget: HTMLInputElement; target: Element }
    );
  };

  const handleKeyDown: JSX.EventHandlerUnion<
    HTMLInputElement,
    KeyboardEvent
  > = (e) => {
    emit('tc-custominput-keydown', e as KeyboardEvent);
    props.onKeyDown?.(
      e as KeyboardEvent & { currentTarget: HTMLInputElement; target: Element }
    );
  };

  return (
    <div class={`tc-custom-field ${props.class || ''}`.trim()}>
      <label for={getId()}>{props.label}</label>
      <div class="input-focus-group relative">
        <input
          id={getId()}
          name={props.name}
          class={inputClass()}
          ref={(el) => (inputRef = el)}
          type={getType()}
          value={getValue()}
          placeholder={props.placeholder}
          disabled={props.disabled}
          readonly={props.readonly}
          required={props.required}
          autocomplete={props.autocomplete === false ? 'off' : undefined}
          data-cy={getDataCy()}
          data-testid={getDataCy()}
          onInput={handleInput}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
        />
        <div class="line" />
      </div>
      {hasError() && <div class="error-message">{props.error}</div>}
    </div>
  );
};

export default CustomInput;
